import { RouteIcon } from 'lucide-react';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { HandoffForm } from '@/Domain/components/Action/HandoffAction/HandoffAction.sheet';
import { Banner } from '@/Domain/components/Shared/Banner/Banner';
import { FormControl, FormField, FormMessage } from '@/Domain/components/Shared/Form/Form';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import { useScenarioUsers } from '@/Domain/hooks/AI/useScenarioUsers.hook';

type Props = {
  form: UseFormReturn<HandoffForm>;
};

export const TopicRoutingField = ({ form }: Props) => {
  const { t } = useTranslation('handoff_action');
  const { users, isLoading } = useScenarioUsers();

  const { fields } = useFieldArray({
    name: 'routing_profiles',
    control: form.control,
  });

  return (
    <div id="topic_routing" data-testid="topic-routing-section">
      <Banner
        className="!items-start"
        Icon={RouteIcon}
        bgIconColor="bg-blue-500"
        title={t('form.topic_routing_label')}
        description={t('form.topic_routing_description')}
      />

      <div className="mt-4 space-y-3 px-3" data-testid="topic-routing-users">
        {isLoading && (
          <p className="text-sm text-foreground-subtle">{t('form.topic_routing_loading')}</p>
        )}

        {!isLoading && fields.length === 0 && (
          <p className="text-sm text-foreground-subtle" data-testid="topic-routing-no-users">
            {t('form.topic_routing_no_users')}
          </p>
        )}

        {fields.map((field, index) => {
          const user = users?.find((u) => u.id === field.user_id);
          if (!user) return null;

          return (
            <div
              key={field.id}
              className="grid grid-cols-[1fr_2fr_auto] gap-3 items-start"
              data-testid={`routing-profile-${field.user_id}`}
            >
              <div className="text-sm pt-2">
                <p className="font-medium truncate">
                  {user.first_name} {user.last_name}
                </p>
                <p className="text-foreground-subtle truncate text-xs">{user.email}</p>
              </div>

              <FormField
                name={`routing_profiles.${index}.routing_description`}
                render={({ field: inputField }) => (
                  <div>
                    <FormControl>
                      <Input
                        {...inputField}
                        placeholder={t('form.topic_routing_user_description_placeholder')}
                        maxLength={150}
                        data-testid={`routing-description-${field.user_id}`}
                      />
                    </FormControl>
                    <FormMessage className="mt-1.5" />
                  </div>
                )}
              />

              <FormField
                name={`routing_profiles.${index}.is_active`}
                render={({ field: switchField }) => (
                  <div className="pt-2">
                    <Switch
                      data-testid={`routing-active-${field.user_id}`}
                      checked={switchField.value}
                      onCheckedChange={switchField.onChange}
                    />
                  </div>
                )}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
